import { useState, useEffect, useRef } from 'react';

/**
 * 통화 모달의 소리 칸 역할 판정 훅 (1:1 통화 및 단체 통화 공용)
 * 
 * [소리 칸 판정 기준 - 기존 VoiceCallModal 로직 100% 보존]
 * 1. HTMLMediaElement.prototype.setSinkId 메서드 존재 여부 (동기 평가)
 * 2. navigator.mediaDevices.enumerateDevices 존재 여부 (동기 평가)
 * 3. audiooutput 장치 목록 2개 이상 존재 여부 (비동기 평가)
 * 
 * [반환값: outputRole]
 * - 'speaker': 스피커/출력 전환 지원 환경 (데스크톱 엣지/크롬 등 setSinkId 지원 및 출력 장치 2개 이상)
 * - 'reconnect': 모바일 웹/PWA 등 출력 전환 미지원 환경 (이어폰/블루투스 마이크 재연결 버튼 제공)
 * // TODO: 향후 네이티브 앱(TWA/Capacitor) 연동 시 OS AudioManager 제어용 'native' 추가 예정
 * 
 * @param {boolean} isVisible 통화 모달 화면 표시 여부 (표시 중에만 비동기 장치 검사 수행)
 * @param {boolean} isConnected 통화 연결 상태 여부 (연결 직후 1회 재판정용)
 * @returns {{ outputRole: 'speaker' | 'reconnect', isSpeakerSupported: boolean }}
 */
export function useAudioOutputSupport(isVisible = true, isConnected = false) {
  const isSinkIdSupported = typeof HTMLMediaElement !== 'undefined' && typeof HTMLMediaElement.prototype.setSinkId === 'function';
  const hasEnumerateDevices = typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.enumerateDevices);

  // 동기 판정: setSinkId와 enumerateDevices가 없으면 즉시 false (깜빡임 방지)
  const [isSpeakerSupported, setIsSpeakerSupported] = useState(() => isSinkIdSupported && hasEnumerateDevices);
  const reevaluatedRef = useRef(false);

  // 비동기 판정: 실제 출력 장치(audiooutput >= 2) 검사 (기존 마운트 effect 보존)
  useEffect(() => {
    if (!isVisible || !isSinkIdSupported || !hasEnumerateDevices) {
      return;
    }

    let isMounted = true;
    navigator.mediaDevices.enumerateDevices()
      .then((devices) => {
        if (!isMounted) return;
        const audioOutputs = devices.filter((d) => d.kind === 'audiooutput');
        if (audioOutputs.length < 2) {
          setIsSpeakerSupported(false);
        }
      })
      .catch((err) => {
        console.warn('[useAudioOutputSupport] enumerateDevices failed:', err);
        if (isMounted) setIsSpeakerSupported(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isVisible, isSinkIdSupported, hasEnumerateDevices]);

  // 연결 직후 1회 한정 재판정 (B-1a §8-3 ④: 수신 측 false -> true 복구 허용)
  useEffect(() => {
    if (!isConnected) {
      reevaluatedRef.current = false;
      return;
    }

    if (reevaluatedRef.current) {
      return;
    }

    if (!isSinkIdSupported || !hasEnumerateDevices) {
      return;
    }

    reevaluatedRef.current = true;
    let isMounted = true;

    navigator.mediaDevices.enumerateDevices()
      .then((devices) => {
        if (!isMounted) return;
        const audioOutputs = devices.filter((d) => d.kind === 'audiooutput');
        const isSupported = audioOutputs.length >= 2;
        setIsSpeakerSupported(isSupported);
        if (import.meta.env.DEV) {
          console.info('[소리 칸] 연결 직후 재판정:', audioOutputs.length, isSupported);
        }
      })
      .catch((err) => {
        console.warn('[useAudioOutputSupport] enumerateDevices failed:', err);
        if (isMounted) setIsSpeakerSupported(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isConnected, isSinkIdSupported, hasEnumerateDevices]);

  const outputRole = isSpeakerSupported ? 'speaker' : 'reconnect';

  return {
    outputRole,
    isSpeakerSupported,
  };
}

export default useAudioOutputSupport;
